import { useState, useEffect, useCallback } from 'react';
import {
  ChatCircle, User, EnvelopeSimple, Check, X, CircleNotch,
  PaperPlaneRight, ArrowRight, Eye, Clock, Buildings, Warning
} from '@phosphor-icons/react';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { TranslateButton, TranslateBackButton } from '../components/TranslateButton';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_STYLES = {
  open: { bg: 'bg-amber-100 text-amber-800', label: 'Open' },
  assigned: { bg: 'bg-blue-100 text-blue-700', label: 'Toegewezen' },
  closed: { bg: 'bg-zinc-100 text-zinc-600', label: 'Gesloten' },
};

const CATEGORY_STYLES = {
  quote_request: { bg: 'bg-emerald-50 text-emerald-700', label: 'Offerte' },
  order: { bg: 'bg-teal-50 text-teal-700', label: 'Bestelling' },
  question: { bg: 'bg-cyan-50 text-cyan-700', label: 'Vraag' },
  interest: { bg: 'bg-blue-50 text-blue-700', label: 'Interesse' },
  unsubscribe: { bg: 'bg-orange-50 text-orange-700', label: 'Afmelding' },
};

export default function ConversationsPage() {
  const [convs, setConvs] = useState([]);
  const [stats, setStats] = useState(null);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [filter, setFilter] = useState('');
  const [replyText, setReplyText] = useState('');
  const [replySending, setReplySending] = useState(false);
  const [translatedMsg, setTranslatedMsg] = useState({});

  const fetchConvs = useCallback(() => {
    const params = filter ? `?status=${filter}` : '';
    fetch(`${API}/conversations${params}`, { credentials: 'include' })
      .then(r => r.json()).then(d => { setConvs(d.conversations || []); setTotal(d.total || 0); })
      .catch(() => {}).finally(() => setLoading(false));
  }, [filter]);

  const fetchStats = useCallback(() => {
    fetch(`${API}/conversations/stats`, { credentials: 'include' })
      .then(r => r.json()).then(setStats).catch(() => {});
  }, []);

  useEffect(() => { fetchConvs(); fetchStats(); }, [fetchConvs, fetchStats]);

  const openDetail = (conv) => {
    setSelected(conv.conversation_id);
    fetch(`${API}/conversations/${conv.conversation_id}`, { credentials: 'include' })
      .then(r => r.json()).then(setDetail).catch(() => {});
    setReplyText('');
    setTranslatedMsg({});
  };

  const closeDetail = () => { setSelected(null); setDetail(null); };

  const updateStatus = async (convId, status) => {
    await fetch(`${API}/conversations/${convId}`, {
      method: 'PUT', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ status }),
    });
    fetchConvs(); fetchStats();
    if (detail?.conversation_id === convId) {
      setDetail(d => ({ ...d, status }));
    }
  };

  const sendReply = async () => {
    if (!replyText.trim() || !detail) return;
    setReplySending(true);
    await fetch(`${API}/conversations/${detail.conversation_id}/reply`, {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ body_text: replyText, send: true, subject: `Re: ${detail.subject || ''}` }),
    });
    setReplyText('');
    // Refresh detail
    const res = await fetch(`${API}/conversations/${detail.conversation_id}`, { credentials: 'include' });
    setDetail(await res.json());
    fetchConvs();
    setReplySending(false);
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="w-6 h-6 border-2 border-[#0055FF] border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="animate-fade-in" data-testid="conversations-page">
      <div className="mb-6">
        <h1 className="font-heading text-3xl font-black tracking-tight">Conversaties</h1>
        <p className="text-sm text-zinc-500 mt-1">Replies van contacten — sequence gestopt, klaar voor handmatige opvolging</p>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-zinc-200 border border-zinc-200 mb-6" data-testid="conv-stats">
          {[
            { l: 'Totaal', v: stats.total, c: 'text-zinc-700' },
            { l: 'Open', v: stats.open, c: stats.open > 0 ? 'text-amber-600' : 'text-zinc-400' },
            { l: 'Toegewezen', v: stats.assigned, c: 'text-blue-600' },
            { l: 'Gesloten', v: stats.closed, c: 'text-emerald-600' },
          ].map((s, i) => (
            <div key={i} className="bg-white p-3 text-center">
              <div className={`font-data text-xl font-bold ${s.c}`}>{s.v}</div>
              <div className="text-[10px] text-zinc-500 uppercase tracking-wider">{s.l}</div>
            </div>
          ))}
        </div>
      )}

      {/* Filter */}
      <div className="flex items-center gap-3 mb-4">
        <Select value={filter || 'all'} onValueChange={v => setFilter(v === 'all' ? '' : v)}>
          <SelectTrigger className="w-48 rounded-sm text-sm" data-testid="conv-filter"><SelectValue placeholder="Alle statussen" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Alle</SelectItem>
            <SelectItem value="open">Open</SelectItem>
            <SelectItem value="assigned">Toegewezen</SelectItem>
            <SelectItem value="closed">Gesloten</SelectItem>
          </SelectContent>
        </Select>
        <span className="text-xs text-zinc-400">{total} conversaties</span>
      </div>

      {/* Table */}
      <div className="border border-zinc-200 overflow-hidden" data-testid="conv-table">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-zinc-50 border-b border-zinc-200">
              <th className="text-left px-4 py-2 text-xs font-semibold uppercase tracking-widest text-zinc-500">Contact</th>
              <th className="text-left px-4 py-2 text-xs font-semibold uppercase tracking-widest text-zinc-500">Onderwerp</th>
              <th className="text-left px-4 py-2 text-xs font-semibold uppercase tracking-widest text-zinc-500">Categorie</th>
              <th className="text-center px-4 py-2 text-xs font-semibold uppercase tracking-widest text-zinc-500">Berichten</th>
              <th className="text-left px-4 py-2 text-xs font-semibold uppercase tracking-widest text-zinc-500">Status</th>
              <th className="text-left px-4 py-2 text-xs font-semibold uppercase tracking-widest text-zinc-500">Datum</th>
            </tr>
          </thead>
          <tbody>
            {convs.length === 0 ? (
              <tr><td colSpan={6} className="px-4 py-12 text-center text-zinc-400">
                <ChatCircle size={32} className="mx-auto mb-2 text-zinc-300" />
                <div className="text-sm">Geen conversaties{filter ? ' met deze filter' : ''}.</div>
                <div className="text-xs mt-1">Conversaties worden aangemaakt wanneer contacten reageren op campagne-emails.</div>
              </td></tr>
            ) : convs.map(c => {
              const st = STATUS_STYLES[c.status] || STATUS_STYLES.open;
              const cat = CATEGORY_STYLES[c.category] || {};
              return (
                <tr key={c.conversation_id} onClick={() => openDetail(c)}
                  className={`border-b border-zinc-100 cursor-pointer transition-colors ${selected === c.conversation_id ? 'bg-blue-50' : 'hover:bg-zinc-50'}`}
                  data-testid={`conv-row-${c.conversation_id}`}>
                  <td className="px-4 py-2.5">
                    <div className="text-sm font-medium">{c.from_name || c.from_email?.split('@')[0]}</div>
                    <div className="text-[10px] text-zinc-400">{c.from_email}</div>
                  </td>
                  <td className="px-4 py-2.5 text-sm max-w-[200px] truncate">{c.subject}</td>
                  <td className="px-4 py-2.5">
                    {cat.label && <Badge className={`rounded-sm px-2 py-0.5 text-[10px] ${cat.bg}`}>{cat.label}</Badge>}
                  </td>
                  <td className="px-4 py-2.5 text-center font-data">{c.messages?.length || 1}</td>
                  <td className="px-4 py-2.5"><Badge className={`rounded-sm px-2 py-0.5 text-[10px] ${st.bg}`}>{st.label}</Badge></td>
                  <td className="px-4 py-2.5 text-xs text-zinc-500">{c.created_at ? new Date(c.created_at).toLocaleDateString('nl-NL', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '-'}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Detail Panel */}
      {detail && (
        <div className="fixed inset-0 bg-black/40 z-50 flex justify-end" onClick={closeDetail}>
          <div className="w-full max-w-2xl bg-white h-full overflow-y-auto shadow-2xl animate-slide-in-right" onClick={e => e.stopPropagation()} data-testid="conv-detail">
            {/* Header */}
            <div className="sticky top-0 bg-white border-b border-zinc-200 px-6 py-4 flex items-center justify-between z-10">
              <div>
                <div className="font-medium text-sm">{detail.from_name || detail.from_email}</div>
                <div className="text-xs text-zinc-400">{detail.subject}</div>
              </div>
              <div className="flex items-center gap-2">
                <Select value={detail.status} onValueChange={v => updateStatus(detail.conversation_id, v)}>
                  <SelectTrigger className="w-32 h-8 rounded-sm text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="open">Open</SelectItem>
                    <SelectItem value="assigned">Toegewezen</SelectItem>
                    <SelectItem value="closed">Gesloten</SelectItem>
                  </SelectContent>
                </Select>
                <button onClick={closeDetail} className="p-1.5 hover:bg-zinc-100 rounded-sm"><X size={18} /></button>
              </div>
            </div>

            <div className="px-6 py-5 space-y-4">
              {/* Info */}
              <div className="flex gap-3 text-xs text-zinc-500">
                <span><EnvelopeSimple size={12} className="inline mr-1" />{detail.from_email}</span>
                {detail.cancelled_steps > 0 && <span className="text-amber-600"><Warning size={12} className="inline mr-1" />{detail.cancelled_steps} emails geannuleerd</span>}
              </div>

              {/* Messages */}
              <div className="space-y-3">
                {(detail.messages || []).map((msg, i) => (
                  <div key={i} className={`rounded-sm p-3 ${msg.direction === 'inbound' ? 'bg-zinc-50 border border-zinc-200' : 'bg-blue-50 border border-blue-200'}`}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs font-medium">{msg.direction === 'inbound' ? (detail.from_name || 'Contact') : 'PoolFIX (jij)'}</span>
                      <div className="flex items-center gap-2">
                        {msg.direction === 'inbound' && (
                          <TranslateButton text={msg.body_text} onTranslated={t => setTranslatedMsg(m => ({ ...m, [i]: t }))} size="sm" />
                        )}
                        <span className="text-[10px] text-zinc-400">{msg.timestamp ? new Date(msg.timestamp).toLocaleString('nl-NL') : ''}</span>
                      </div>
                    </div>
                    <div className="text-sm text-zinc-700 whitespace-pre-wrap leading-relaxed">
                      {translatedMsg[i] || msg.body_text}
                    </div>
                    {translatedMsg[i] && (
                      <div className="text-[10px] text-indigo-500 cursor-pointer hover:underline mt-1" onClick={() => setTranslatedMsg(m => { const n = { ...m }; delete n[i]; return n; })}>Toon origineel</div>
                    )}
                  </div>
                ))}
              </div>

              {/* Reply */}
              {detail.status !== 'closed' && (
                <div className="border-t border-zinc-200 pt-4">
                  <label className="text-xs font-medium text-zinc-500 mb-1 block">Antwoord</label>
                  <textarea value={replyText} onChange={e => setReplyText(e.target.value)}
                    className="w-full rounded-sm border border-zinc-300 p-3 text-sm min-h-[80px] focus:ring-1 focus:ring-[#0055FF] focus:border-[#0055FF] mb-2"
                    placeholder="Typ je antwoord..." data-testid="conv-reply-input" />
                  <div className="flex gap-2">
                    <button onClick={sendReply} disabled={replySending || !replyText.trim()} data-testid="conv-send-reply"
                      className="flex items-center gap-2 px-4 py-2 text-sm font-medium bg-[#0055FF] text-white rounded-sm hover:bg-blue-700 disabled:opacity-50 transition-colors">
                      {replySending ? <CircleNotch size={14} className="animate-spin" /> : <PaperPlaneRight size={14} />}
                      {replySending ? 'Verzenden...' : 'Verzenden'}
                    </button>
                    <button onClick={() => updateStatus(detail.conversation_id, 'closed')}
                      className="px-4 py-2 text-sm text-zinc-600 hover:bg-zinc-100 rounded-sm transition-colors">
                      Sluiten
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
